import React, { useState, useEffect, KeyboardEvent } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { Plus, Edit, Archive, ArchiveRestore, Monitor } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
// 1. Import the Tabs components from your UI library
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

interface ExternalPartner {
    id: string;
    name: string;
    active: boolean;
}

// Helper component to render the table for a given list of partners
// This avoids duplicating the entire table structure
const PartnerTable = ({ partners, onEdit, onArchive }: { partners: ExternalPartner[], onEdit: (partner: ExternalPartner) => void, onArchive: (partner: ExternalPartner) => void }) => {
    return (
        <div className="rounded-md border">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[120px]">Status</TableHead>
                        <TableHead>Partner Name</TableHead>
                        <TableHead className="w-[100px] text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {partners.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={3} className="text-center h-24">
                                No partners found.
                            </TableCell>
                        </TableRow>
                    ) : (
                        partners
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((partner) => (
                                <TableRow key={partner.id} className="h-16">
                                    <TableCell className="py-4">
                                        <div className="flex items-center gap-2">
                                            <div className={`h-2.5 w-2.5 rounded-full ${partner.active ? "bg-green-500" : "bg-gray-300"}`} />
                                            <span>{partner.active ? "Active" : "Archived"}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="py-4">
                                        <div className="flex items-center gap-2">
                                            <Monitor size={16} className="text-muted-foreground shrink-0" />
                                            <span>{partner.name}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right py-4">
                                        <div className="flex justify-end gap-2">
                                            {partner.active && (
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    onClick={() => onEdit(partner)}
                                                >
                                                    <Edit size={16} />
                                                </Button>
                                            )}
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className={partner.active ? "text-destructive" : "text-muted-foreground"}
                                                onClick={() => onArchive(partner)}
                                            >
                                                {partner.active ? <Archive size={16} /> : <ArchiveRestore size={16} />}
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                    )}
                </TableBody>
            </Table>
        </div>
    );
};


export default function ExternalPartnerList() {
    const [partners, setPartners] = useState<ExternalPartner[]>([]);
    const [newPartner, setNewPartner] = useState({ name: '', active: true });
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
    const [editingPartner, setEditingPartner] = useState<ExternalPartner | null>(null);
    const [archivingPartner, setArchivingPartner] = useState<ExternalPartner | null>(null);
    // 2. Add state to manage the active tab
    const [activeTab, setActiveTab] = useState("active");

    useEffect(() => {
        fetchPartners();
    }, []);

    const fetchPartners = async () => {
        try {
            const response = await fetch(`${API_BASE}/api/external-partners`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                setPartners(data);
            } else {
                const errorData = await response.json().catch(() => ({}));
                toast.error(errorData.message || 'Failed to fetch external partners');
            }
        } catch (error) {
            console.error('Error fetching external partners:', error);
            toast.error('Failed to fetch external partners');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newPartner.name.trim()) {
            toast.error("Partner name is required");
            return;
        }
        try {
            const response = await fetch(`${API_BASE}/api/external-partners`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(newPartner)
            });
            if (response.ok) {
                toast.success("External partner added successfully");
                setNewPartner({ name: '', active: true });
                setIsDialogOpen(false);
                await fetchPartners();
                setActiveTab("active"); // Switch to active tab after adding
            } else {
                const errorData = await response.json().catch(() => ({}));
                toast.error(errorData.message || "Failed to add external partner");
            }
        } catch (error) {
            toast.error("Failed to add external partner");
        }
    };

    const handleEdit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingPartner || !editingPartner.name.trim()) {
            toast.error("Partner name is required");
            return;
        }
        try {
            const response = await fetch(`${API_BASE}/api/external-partners/${editingPartner.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ name: editingPartner.name })
            });
            if (response.ok) {
                toast.success("Partner updated successfully");
                setEditingPartner(null);
                setIsEditDialogOpen(false);
                fetchPartners();
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || "Failed to update partner");
            }
        } catch (error) {
            toast.error("Failed to update partner");
        }
    };

    const handleArchive = async () => {
        if (!archivingPartner?.id) return;
        try {
            const response = await fetch(`${API_BASE}/api/external-partners/${archivingPartner.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ active: !archivingPartner.active })
            });
            if (response.ok) {
                toast.success(`Partner ${archivingPartner.active ? 'archived' : 'unarchived'} successfully`);
                setArchivingPartner(null);
                setIsArchiveDialogOpen(false);
                await fetchPartners();
                // Switch to the relevant tab after action
                setActiveTab(archivingPartner.active ? "archived" : "active");
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || `Failed to ${archivingPartner.active ? 'archive' : 'unarchive'} partner`);
            }
        } catch (error) {
            toast.error(`Failed to ${archivingPartner.active ? 'archive' : 'unarchive'} partner`);
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, submitFn: () => void) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            submitFn();
        }
    };

    // 3. Filter partners into active and archived lists
    const activePartners = partners.filter(p => p.active);
    const archivedPartners = partners.filter(p => !p.active);

    return (
        <>
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>External Partners</CardTitle>
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                            <Button>
                                <Plus size={16} className="mr-2" />
                                Add Partner
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Add New External Partner</DialogTitle>
                                <DialogDescription>Add a new external partner that can be referenced in activities.</DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleSubmit}>
                                <div className="space-y-4 py-4">
                                    <div className="space-y-2">
                                        <div className="flex justify-between items-center">
                                            <Label htmlFor="partnerName">Partner Name</Label>
                                            <p className={`text-sm ${newPartner.name.length > 50 ? 'text-red-500' : 'text-muted-foreground'}`}>
                                                Character limit {newPartner.name.length} / 50
                                            </p>
                                        </div>
                                        <Input
                                            id="partnerName"
                                            value={newPartner.name}
                                            onChange={(e) => {
                                                const truncatedValue = e.target.value.slice(0, 50);
                                                setNewPartner({ ...newPartner, name: truncatedValue });
                                            }}
                                            onKeyDown={(e) => handleKeyDown(e, () => (e.currentTarget.form?.querySelector('button[type="submit"]') as HTMLButtonElement)?.click())}
                                            placeholder="Enter partner name"
                                            required
                                            maxLength={50}
                                        />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
                                    {/* 4. Disable the submit button if the name is empty */}
                                    <Button type="submit" disabled={!newPartner.name.trim()}>Add Partner</Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </CardHeader>
                <CardContent>
                    {/* 4. Implement the Tabs component */}
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="active">Active ({activePartners.length})</TabsTrigger>
                            <TabsTrigger value="archived">Archived ({archivedPartners.length})</TabsTrigger>
                        </TabsList>
                        <TabsContent value="active">
                            <PartnerTable
                                partners={activePartners}
                                onEdit={(partner) => { setEditingPartner(partner); setIsEditDialogOpen(true); }}
                                onArchive={(partner) => { setArchivingPartner(partner); setIsArchiveDialogOpen(true); }}
                            />
                        </TabsContent>
                        <TabsContent value="archived">
                            <PartnerTable
                                partners={archivedPartners}
                                onEdit={(partner) => { setEditingPartner(partner); setIsEditDialogOpen(true); }}
                                onArchive={(partner) => { setArchivingPartner(partner); setIsArchiveDialogOpen(true); }}
                            />
                        </TabsContent>
                    </Tabs>
                </CardContent>
            </Card>

            {/* Edit Dialog */}
            {editingPartner && (
                <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Edit Partner</DialogTitle>
                            <DialogDescription>Update the partner's information.</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleEdit}>
                            <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="editPartnerName">Partner Name</Label>
                                    <Input
                                        id="editPartnerName"
                                        value={editingPartner.name}
                                        onChange={(e) => setEditingPartner({ ...editingPartner, name: e.target.value })}
                                        onKeyDown={(e) => handleKeyDown(e, () => (e.currentTarget.form?.querySelector('button[type="submit"]') as HTMLButtonElement)?.click())}
                                        placeholder="Enter partner name"
                                        required
                                    />
                                </div>
                            </div>
                            <DialogFooter>
                                <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
                                <Button type="submit">Save Changes</Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            )}

            {/* Archive/Unarchive Dialog */}
            {archivingPartner && (
                <Dialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>{archivingPartner.active ? 'Archive' : 'Unarchive'} Partner</DialogTitle>
                            <DialogDescription>Are you sure you want to {archivingPartner.active ? 'archive' : 'unarchive'} the partner "{archivingPartner.name}"?</DialogDescription>
                        </DialogHeader>
                        <div className="py-4">
                            <p className="text-muted-foreground">
                                {archivingPartner.active
                                    ? "Archiving a partner will make it unavailable for new activities."
                                    : "Unarchiving a partner will make it available for use in activities again."}
                            </p>
                        </div>
                        <DialogFooter>
                            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                            <Button variant={archivingPartner.active ? "destructive" : "default"} onClick={handleArchive}>
                                {archivingPartner.active ? 'Archive' : 'Unarchive'} Partner
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}
